import { type ApolloClient, gql, type NormalizedCacheObject } from '@apollo/client/core';
import type { VPResult } from '../../VPResult';
import { type InternalApiError, toApiErrors } from '../errors';
import { CONNECTION_METADATA_FIELDS, type ConnectionArguments } from '../../connection/Api';
import * as User from './User';
import type * as UserSearchArguments from './UserSearchArguments';
import { INTERNAL_USER_FIELDS } from './fragments';
import { ACCOUNT_AGNOSTIC } from '../../accountContext';

/**
 * The staff user lookup: find a person by name or email across *every* account, and see which
 * accounts they belong to.
 *
 * It lives on the internal (VP staff) schema rather than AppSchema (ADR-009) for the reason the
 * shape makes obvious — it reads users and memberships regardless of tenancy, which is exactly what
 * should not sit in a schema published to every account holder. Auth is the superadmin Rails
 * session cookie: a bearer token never satisfies the gate, and it only works in the Rails-embedded
 * admin against a same-origin `/graphql`, never the standalone dev server — so this goes through
 * `internalGqlApiClient`.
 *
 * A non-superadmin caller is rejected with HTTP 401 before the query is parsed, which Apollo
 * *throws* rather than returning a payload. The read below therefore catches and classifies via
 * `toApiErrors`, so the screen can branch on `isUnauthorized` and say "VenuePilot staff only"
 * instead of rendering an empty table — and so an expected authorization outcome does not reach
 * Sentry as an exception. **Nothing here throws.**
 *
 * Read-only: the lookup links out to the account screens for anything that changes state, so there
 * is no mutation here and no `MutationResult` counterpart.
 */

const INTERNAL_USERS_QUERY = gql`
  query InternalUsers($after: String, $before: String, $first: Int, $last: Int, $filter: UserSearchArguments) {
    users(after: $after, before: $before, first: $first, last: $last, filter: $filter) {
      edges {
        cursor
        node {
          ...InternalUserFields
        }
      }
      pageInfo {
        ...ConnectionMetadataFields
      }
    }
  }
  ${INTERNAL_USER_FIELDS}
  ${CONNECTION_METADATA_FIELDS}
`;

export const fetchUsers = async (
  client: ApolloClient<NormalizedCacheObject>,
  { pagination, filter }: {
    pagination?: ConnectionArguments;
    filter?: UserSearchArguments.UserSearchArguments;
  } = {},
): Promise<VPResult<User.UserConnection, InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_USERS_QUERY,
      // A lookup answered from a cache that predates a membership change would send the operator to
      // the wrong account, which is the one thing this screen exists to get right.
      fetchPolicy: 'network-only',
      // Account-agnostic: a cross-account staff lookup. `UserSearchArguments` is `{ id, q }`.
      context: ACCOUNT_AGNOSTIC,
      variables: {
        after: pagination?.after,
        before: pagination?.before,
        first: pagination?.first,
        last: pagination?.last,
        // The first read has no search term, and `null` would be rejected against the input type.
        filter: filter ?? {},
      },
    });

    // `users` is non-null on the schema, so this is only reachable if a resolver soft-fails —
    // resolving it as an empty page would render "no users" and hide the failure.
    if (!data?.users) {
      return { success: false, errors: [{ path: '', message: 'Failed to fetch users.' }] };
    }

    return { success: true, data: User.fromGqlConnection(data.users) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};
